import React from 'react'

import { useNavigate } from 'react-router-dom';
import { SupplierInfoModal } from '../components/RecommendedSuppliers.jsx';
import AdBlockMediaViewer from '../components/AdBlockMediaViewer.jsx';
import { optimizeImageSrc } from '../utils/shared.js';
// c:\xampp\htdocs\miners-market-main\pages\AppLandingPage.js

export default function AppLandingPage({ onLogin, onViewProduct, onAddToBasket, user, basket }) {
    const navigate = useNavigate();
    const [featuredProducts, setFeaturedProducts] = React.useState([]);

    const cleanPath = (path) => {
        if (!path) return null;
        if (path.match(/^(http|https|data):/i)) return path;
        let clean = decodeURIComponent(path).replace(/\\/g, '/').trim();
        if (clean.startsWith('/miners-market-main/')) {
            clean = clean.substring('/miners-market-main/'.length);
        }
        // Ensure path is absolute from web root (required since app is served from /app/ base)
        if (!clean.startsWith('/')) {
            clean = '/' + clean;
        }
        const anchorMatch = clean.match(/(uploads\/|images\/|miscellaneous items\/)/i);
        if (anchorMatch) {
            return clean.substring(anchorMatch.index - 1);
        }
        if (clean.includes(':')) return null;
        return clean;
    };
    const [deals, setDeals] = React.useState([]);
    const [suppliers, setSuppliers] = React.useState([]);
    const [itemOfTheDay, setItemOfTheDay] = React.useState(null);
    const [adBlocks, setAdBlocks] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [currentHeroSlide, setCurrentHeroSlide] = React.useState(0);
    const [expandedCategory, setExpandedCategory] = React.useState(null);
    const [selectedSupplier, setSelectedSupplier] = React.useState(null);
    const [viewerBlock, setViewerBlock] = React.useState(null);

    // Statistics Data (WeBuyCars inspired)
    const statistics = [
        { icon: 'fas fa-truck', label: 'Equipment Listings', value: '8500+', description: 'Active listings' },
        { icon: 'fas fa-users', label: 'Verified Buyers', value: '12,450+', description: 'Registered buyers' },
        { icon: 'fas fa-store', label: 'Trusted Sellers', value: '500+', description: 'Verified sellers' },
        { icon: 'fas fa-globe', label: 'Coverage', value: 'Global', description: 'Worldwide reach' },
        { icon: 'fas fa-clock', label: 'Quick Response', value: '24h', description: 'Average response time' }
    ];

    // Remove auto-rotating carousel since we're using static hero

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                // Corrected path: removed subfolder prefix as app is at root
                // Increased limit to 200 to ensure enough products after filtering
                // Fetch all products and filter in frontend (simpler and more reliable)
                // Add main_categories parameter to include all product tables
                const allRes = await fetch('/api/products.php?limit=200&sort=date_desc&main_categories=equipment,consumables,components,diesel,rebuilds,rentals,commercial,services');
                
                const contentType = allRes.headers.get("content-type");
                if (!allRes.ok || !contentType || !contentType.includes("application/json")) {
                    const errorText = await allRes.text();
                    throw new Error(`Invalid products response (${allRes.status}): ${errorText.substring(0, 100)}`);
                }

                const allData = await allRes.json();
                const allItems = allData.products || [];

                console.log('[AppLandingPage] Total items fetched:', allItems.length);
                console.log('[AppLandingPage] Sample item:', allItems[0]);
                
                // Log all unique categories to see what's available
                const uniqueCategories = [...new Set(allItems.map(item => item.category))];
                console.log('[AppLandingPage] Unique categories:', uniqueCategories);
                
                // Log all unique listing_market values
                const uniqueMarkets = [...new Set(allItems.map(item => item.listing_market))];
                console.log('[AppLandingPage] Unique listing_market values:', uniqueMarkets);

                // Only exclude bearings, filters, and conveyor belts as requested
                const eligibleItems = allItems.filter(item => {
                    // Check by bearing_type field (most reliable for bearings)
                    if (item.bearing_type) return false;
                    // Also check by category_name field for bearings
                    if (item.category_name && item.category_name.toLowerCase().includes('bearing')) return false;
                    // Exclude filters category
                    if (item.category === 'filters') return false;
                    // Also check by category_name for filters
                    if (item.category_name && item.category_name.toLowerCase().includes('filter')) return false;
                    // Exclude conveyor-belts category
                    if (item.category === 'conveyor-belts') return false;
                    // Also check by category_name for conveyor belts
                    if (item.category_name && item.category_name.toLowerCase().includes('conveyor')) return false;
                    return true;
                });
                
                console.log('[AppLandingPage] Eligible items (no bearings/filters/conveyor-belts):', eligibleItems.length);
                
                // Use all eligible items
                const itemsToUse = eligibleItems;
                console.log('[AppLandingPage] Items to use:', itemsToUse.length);

                // --- Seeded Random for 24-hour rotation ---
                // Use current date as seed to ensure same products shown throughout the day
                const today = new Date();
                const daySeed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
                
                // Seeded random function
                const seededRandom = (seed) => {
                    const x = Math.sin(seed++) * 10000;
                    return x - Math.floor(x);
                };
                
                // Shuffle array using seeded random
                const seededShuffle = (array, seed) => {
                    const shuffled = [...array];
                    let currentSeed = seed;
                    for (let i = shuffled.length - 1; i > 0; i--) {
                        const j = Math.floor(seededRandom(currentSeed++) * (i + 1));
                        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                    }
                    return shuffled;
                };
                
                // --- "What's Hot" Strategy: Only equipment, components, and diesel ---
                // Shuffle items with daily seed
                const shuffledTargetItems = seededShuffle(itemsToUse, daySeed);
                setDeals(shuffledTargetItems.slice(0, 12));

                // --- "Recommended for You" Strategy: Only equipment, components, and diesel ---
                // Use different seed offset to get different products than "What's Hot"
                const usedItemIds = new Set(shuffledTargetItems.slice(0, 12).map(item => item.id));
                const remainingTargetItems = itemsToUse.filter(item => !usedItemIds.has(item.id));
                
                console.log('[AppLandingPage] Remaining items for Recommended:', remainingTargetItems.length);
                
                // Shuffle remaining items with a different daily seed
                const shuffledRemainingItems = seededShuffle(remainingTargetItems, daySeed + 1000);
                setFeaturedProducts(shuffledRemainingItems.slice(0, 10));
                
                console.log('[AppLandingPage] Featured products set:', shuffledRemainingItems.slice(0, 10).length);

                // Pick a seeded random item for "Item of the Day" from available items
                if (itemsToUse.length > 0) {
                    const itemOfDayIndex = Math.floor(seededRandom(daySeed + 2000) * itemsToUse.length);
                    setItemOfTheDay(itemsToUse[itemOfDayIndex]);
                }

                // Fetch Suppliers (smaller call)
                const supRes = await fetch('/api/get_suppliers.php');
                
                const supContentType = supRes.headers.get("content-type");
                if (!supRes.ok || !supContentType || !supContentType.includes("application/json")) {
                    const supErrorText = await supRes.text();
                    throw new Error(`Invalid suppliers response (${supRes.status}): ${supErrorText.substring(0, 100)}`);
                }

                const supData = await supRes.json();
                setSuppliers(supData || []);

                // Fetch promotional ad blocks for the landing page
                try {
                    const adRes = await fetch('/api/get_ad_blocks.php');
                    const adData = await adRes.json();
                    const blocksArr = Array.isArray(adData) ? adData : [];

                    // Enrich each block with a LIVE product object sourced from the SAME
                    // products.php endpoint that powers "What's Hot" / "Recommended".
                    // This guarantees the object is fully modal-compatible, so the image
                    // renders and tapping the block opens the correct product card.
                    const enrichedBlocks = await Promise.all(blocksArr.map(async (block) => {
                        let snapshot = null;
                        if (block.product_data) {
                            try {
                                snapshot = typeof block.product_data === 'string'
                                    ? JSON.parse(block.product_data)
                                    : block.product_data;
                            } catch (e) {
                                snapshot = null;
                            }
                        }
                        // Ignore empty/primitive snapshots (older blocks stored no real object).
                        if (!snapshot || typeof snapshot !== 'object') snapshot = null;

                        const productId = block.product_id || (snapshot && snapshot.id);
                        const listingType = snapshot && (snapshot.listingType || snapshot.listing_type);
                        // The block title stores the product name, so it's a reliable
                        // fallback for identifying the product when the snapshot is empty.
                        const wantName = (snapshot && snapshot.name) || block.title || null;

                        // IMPORTANT: product ids can collide across listing tables (e.g. a
                        // standard product and a consumable can share id 620). We therefore
                        // also match on listing type when known, or on name otherwise, so we
                        // never open the wrong product.
                        const matches = (item) => {
                            if (String(item.id) !== String(productId)) return false;
                            if (listingType && item.listingType) return item.listingType === listingType;
                            if (wantName && item.name) return item.name === wantName;
                            return true;
                        };

                        // 1) Try to reuse a product already fetched for the landing page.
                        let live = productId ? allItems.find(matches) : null;

                        // 2) Otherwise, search products.php by name and match by id (+type/name).
                        if (!live && wantName) {
                            try {
                                const sRes = await fetch(`/api/products.php?search=${encodeURIComponent(wantName)}&limit=20&main_categories=equipment,consumables,components,diesel,rebuilds,rentals,commercial,services`);
                                if (sRes.ok) {
                                    const sData = await sRes.json();
                                    const results = sData.products || [];
                                    live = (productId ? results.find(matches) : null)
                                        || results.find(item => wantName && item.name === wantName)
                                        || null;
                                }
                            } catch (e) {
                                // fall through to snapshot
                            }
                        }

                        // 3) Fall back to the stored snapshot (also a products.php object).
                        return { ...block, liveProduct: live || snapshot };
                    }));

                    setAdBlocks(enrichedBlocks);

                    // Track views for each ad block (once per session)
                    try {
                        const viewedBlocks = JSON.parse(sessionStorage.getItem('viewedAdBlocks') || '[]');
                        enrichedBlocks.forEach(block => {
                            if (!viewedBlocks.includes(block.id)) {
                                fetch('/api/track_ad_block_view.php', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ ad_block_id: block.id })
                                }).catch(err => console.error('Failed to track ad block view:', err));
                                viewedBlocks.push(block.id);
                            }
                        });
                        sessionStorage.setItem('viewedAdBlocks', JSON.stringify(viewedBlocks));
                    } catch (e) {
                        console.error('Failed to track ad block views', e);
                    }
                } catch (adErr) {
                    console.error("Failed to fetch ad blocks", adErr);
                }
            } catch (e) {
                console.error("Failed to fetch landing page data", e);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Define the column structure for categories
    const categoryColumns = [
        {
            title: 'Equipment',
            icon: 'fas fa-truck-monster',
            color: 'text-blue-600',
            bg: 'bg-blue-50',
            items: [
                { name: 'All Products', path: '/equipment' },
                { name: 'Construction Equipment', path: '/equipment?category=construction-equipment' },
                { name: 'Plant', path: '/equipment?category=plant' },
                { name: 'Surface Equipment', path: '/equipment?category=surface-equipment' },
                { name: 'Transportation', path: '/equipment?category=transportation' },
                { name: 'Underground Equipment', path: '/equipment?category=underground-equipment' }
            ]
        },
        {
            title: 'Components',
            icon: 'fas fa-cogs',
            color: 'text-emerald-600',
            bg: 'bg-emerald-50',
            items: [
                 { name: 'All Components', path: '/components-and-spares' }
            ]
        },
        {
            title: 'Consumables',
            icon: 'fas fa-box-open',
            color: 'text-red-600',
            bg: 'bg-red-50',
            items: [
                { name: 'All Consumables', path: '/consumables' },
                { name: 'PPE', path: '/consumables?category=ppe' },
                { name: 'Tools', path: '/consumables?category=tools' }
            ]
        },
        {
            title: 'Miscellaneous',
            icon: 'fas fa-circle-notch',
            color: 'text-cyan-600',
            bg: 'bg-cyan-50',
            items: [
                { name: 'All Miscellaneous', path: '/miscellaneous' },
                { name: 'Bearings', path: '/miscellaneous?category=bearings' },
                { name: 'Filters', path: '/miscellaneous?category=filters' },
                { name: 'Conveyor Belts', path: '/miscellaneous?category=conveyor-belts' },
                { name: 'Greases', path: '/miscellaneous?category=greases' },
                { name: 'Diesel', path: '/miscellaneous?category=diesel' }
            ]
        },
        {
            title: 'Rebuilds',
            icon: 'fas fa-tools',
            color: 'text-indigo-600',
            bg: 'bg-indigo-50',
            items: [
                { name: 'All Rebuilds', path: '/rebuilds' }
            ]
        },
        {
            title: 'Rentals',
            icon: 'fas fa-clock',
            color: 'text-orange-600',
            bg: 'bg-orange-50',
            items: [
                { name: 'All Rentals', path: '/rentals' }
            ]
        },
        {
            title: 'Commercial',
            icon: 'fas fa-briefcase',
            color: 'text-purple-600',
            bg: 'bg-purple-50',
            items: [
                { name: 'All Commercial', path: '/commercial' },
                { name: 'Mines for Sale', path: '/commercial?category=mines' },
                { name: 'Ore Sales', path: '/commercial?category=ore-sales' },
                { name: 'Services', path: '/commercial?category=refurbishing-services' }
            ]
        },
        {
            title: 'Financing',
            icon: 'fas fa-hand-holding-usd',
            color: 'text-green-600',
            bg: 'bg-green-50',
            items: [
                { name: 'Finance Form', path: '/financeform' }
            ]
        },
        {
            title: 'Insurance',
            icon: 'fas fa-shield-alt',
            color: 'text-yellow-600',
            bg: 'bg-yellow-50',
            items: [
                { name: 'King Price Insurance', path: '/kp-insurance' }
            ]
        },
        {
            title: 'SHEQ Services',
            icon: 'fas fa-shield-alt',
            color: 'text-blue-600',
            bg: 'bg-blue-50',
            items: [
                { name: 'SHEQ Services', path: '/sheq' }
            ]
        },
        {
            title: 'Drill Catalog',
            icon: 'fas fa-drill',
            color: 'text-orange-600',
            bg: 'bg-orange-50',
            items: [
                { name: 'Drill Catalog', path: '/drill-catalog' }
            ]
        },
        {
            title: 'Labour',
            icon: 'fas fa-users',
            color: 'text-green-600',
            bg: 'bg-green-50',
            items: [
                { name: 'Labour', path: '/labour' }
            ]
        }
    ];

    const handleSearch = (e) => {
        e.preventDefault();
        const term = e.target.search.value;
        if(term) navigate(`/search?search=${encodeURIComponent(term)}`);
    };

    const handleAdBlockClick = (block) => {
        // Track click
        try {
            fetch('/api/track_ad_block_click.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ad_block_id: block.id })
            }).catch(err => console.error('Failed to track ad block click:', err));
        } catch (e) {}

        // If ad block has media files, open the full-screen viewer
        if (block.media && block.media.length > 0) {
            setViewerBlock(block);
            return;
        }
        // Otherwise, fall back to product/link behavior
        let product = block.liveProduct || null;
        if (!product && block.product_data) {
            try {
                product = typeof block.product_data === 'string' ? JSON.parse(block.product_data) : block.product_data;
            } catch (e) {
                console.error('Failed to parse ad block product data', e);
            }
        }
        if (product) {
            onViewProduct(product);
            return;
        }
        if (block.link) {
            let url = block.link;
            if (/^https?:\/\//i.test(url)) {
                openExternalLink(url);
            } else {
                if (!url.startsWith('/')) url = '/' + url;
                navigate(url);
            }
            return;
        }
        // No product, no link, no media — just a simple image/text block.
        // Open viewer anyway so user can see the image full-screen.
        if (block.image) {
            setViewerBlock({
                ...block,
                media: block.image ? [{ id: 'legacy', file_type: 'image', file_path: block.image, file_name: 'ad-image', position: 0 }] : []
            });
        }
    };

    const formatPrice = (price) => {
        if (!price) return 'POR';
        return `R ${parseFloat(price).toLocaleString()}`;
    };

    return (
        <div data-name="app-landing-page" data-file="pages/AppLandingPage.js" className="min-h-screen bg-gray-900 pb-24 font-sans">
            {/* 1. Top Navigation / Search - Glassmorphism effect with dark theme */}
            <div className="bg-gray-900 sticky top-0 z-40 shadow-md px-4 py-3 border-b border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                    <img src={optimizeImageSrc('images/logo/MM Logo.jpg', 100)} alt="Logo" className="h-9 w-9 rounded-lg shadow-sm" />
                    <div className="flex flex-col">
                        <span className="font-extrabold text-white text-lg leading-none tracking-tight">MINERS MARKET</span>
                    </div>
                    <div className="ml-auto flex gap-4">
                        {/* Show login button for non-logged-in users */}
                        {!user && (
                            <button 
                                onClick={() => {
                                    const event = new CustomEvent('showLoginModal', { detail: {} });
                                    window.dispatchEvent(event);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center"
                            >
                                <i className="fas fa-sign-in-alt mr-2"></i>Login
                            </button>
                        )}
                        {/* Show basket and role switcher for logged-in users */}
                        {user && user.activeRole === 'buyer' && (
                            <>
                                <button onClick={() => navigate('/basket', { state: { from: location.pathname } })} className="relative text-white/80 hover:text-white transition-colors">
                                    <i className="fas fa-shopping-basket text-xl"></i>
                                    {basket && basket.length > 0 && (
                                        <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm border border-white">
                                            {basket.reduce((acc, item) => acc + item.quantity, 0)}
                                        </span>
                                    )}
                                </button>
                                {user.roles && user.roles.includes('seller') && (
                                    <button 
                                        onClick={() => {
                                            // Switch to seller role
                                            const event = new CustomEvent('switchRole', { detail: 'seller' });
                                            window.dispatchEvent(event);
                                        }}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors text-sm flex items-center"
                                    >
                                        <i className="fas fa-sync-alt mr-2"></i>Switch to Seller
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className="relative group">
                    <form onSubmit={handleSearch} className="relative">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-white/60"></i>
                        <input 
                            name="search"
                            type="text" 
                            placeholder="Search equipment, parts, services..." 
                            className="w-full bg-white/10 border border-white/20 focus:bg-white/20 focus:border-blue-400 text-white/90 rounded-xl py-2.5 pl-10 pr-4 text-sm focus:outline-none transition-all shadow-inner placeholder-white/50"
                        />
                        <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-600 text-white w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors">
                            <i className="fas fa-search text-xs"></i>
                        </button>
                    </form>
                </div>
            </div>

            {/* 2. Hero Section - Buy/Sell Focus */}
            <div className="relative">
                {/* Background with overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900/20 to-gray-900">
                    <div className="absolute inset-0 bg-black/40"></div>
                </div>
                
                <div className="relative px-4 pt-[80px] pb-12">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                            Your Mining Equipment
                            <span className="block text-blue-400">Marketplace</span>
                        </h1>
                        <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                            Connect with trusted buyers and sellers in the mining industry. 
                            Buy quality equipment or sell your assets with confidence.
                        </p>
                    </div>
                    
                    {/* Buy/Sell Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                        <button 
                            onClick={() => navigate('/equipment')}
                            className="group relative bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-3 min-h-[60px]"
                        >
                            <i className="fas fa-shopping-cart text-2xl"></i>
                            <div className="text-left">
                                <div className="text-xl">Buy Equipment</div>
                                <div className="text-xs opacity-90">Browse 6000+ listings</div>
                            </div>
                            <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                        </button>
                        
                        <button 
                            onClick={() => {
                                console.log('AppLandingPage: Sell Equipment clicked, user:', user);
                                if (user) {
                                    console.log('AppLandingPage: User logged in, navigating to profile');
                                    navigate('/profile');
                                } else {
                                    console.log('AppLandingPage: User not logged in, triggering signup modal');
                                    // Trigger signup modal by dispatching custom event
                                    const event = new CustomEvent('showSignupModal', { detail: { sellerFlow: true } });
                                    console.log('AppLandingPage: Dispatching event:', event);
                                    window.dispatchEvent(event);
                                }
                            }}
                            className="group relative bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-3 min-h-[60px]"
                        >
                            <i className="fas fa-handshake text-2xl"></i>
                            <div className="text-left">
                                <div className="text-xl">Sell Equipment</div>
                                <div className="text-xs opacity-90">Reach verified buyers</div>
                            </div>
                            <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                        </button>
                    </div>
                    
                    {/* Statistics Section */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                        {statistics.map((stat, idx) => (
                            <div key={idx} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                                <div className="text-blue-400 text-2xl mb-2">
                                    <i className={stat.icon}></i>
                                </div>
                                <div className="text-white font-bold text-lg mb-1">{stat.value}</div>
                                <div className="text-gray-300 text-xs font-medium">{stat.label}</div>
                                <div className="text-gray-400 text-xs mt-1">{stat.description}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 2b. Advertising - product-style blocks under the Buy/Sell buttons */}
            {adBlocks.length > 0 && (
                <div className="px-4 mb-8">
                    <h3 className="font-bold text-white text-lg mb-4">Advertising</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {adBlocks.map(block => {
                            // Prefer the freshly-fetched live product (same data used by
                            // "What's Hot" / "Recommended") and fall back to the stored snapshot.
                            let adProduct = block.liveProduct || null;
                            if (!adProduct && block.product_data) {
                                try {
                                    adProduct = typeof block.product_data === 'string' ? JSON.parse(block.product_data) : block.product_data;
                                } catch (e) { adProduct = null; }
                            }
                            if (!adProduct || typeof adProduct !== 'object') adProduct = null;

                            // Image: product image > first media file > legacy block image > placeholder
                            const adImg = adProduct
                                ? (cleanPath(adProduct.image_url_1 || adProduct.image_url_2 || adProduct.image_url_3 || adProduct.image_url_4) || 'images/products/Wooden Box.png')
                                : (block.media && block.media.length > 0 && block.media[0].file_type === 'image'
                                    ? cleanPath('/' + block.media[0].file_path) || '/' + block.media[0].file_path
                                    : (block.image ? cleanPath(block.image) : 'images/products/Wooden Box.png'));

                            const adTitle = adProduct ? adProduct.name : (block.title || '');
                            const adSubtitle = adProduct ? '' : (block.subtitle || '');
                            const adCat = adProduct ? (adProduct.category_name || adProduct.listingType || 'Featured') : 'Featured';
                            const adPrice = adProduct ? adProduct.price : null;
                            return (
                                <div
                                    key={block.id}
                                    onClick={() => handleAdBlockClick(block)}
                                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden cursor-pointer hover:bg-white/20 transition-all flex flex-col h-full"
                                >
                                    <div className="h-40 bg-white/10 relative overflow-hidden">
                                        <img src={optimizeImageSrc(adImg, 400)} alt={adTitle} className="w-full h-full object-cover" onError={(e) => { e.target.src = optimizeImageSrc('images/products/Wooden Box.png', 400); }} />
                                        {/* Media indicator badge */}
                                        {block.media && block.media.length > 0 && (
                                            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                                <i className="fas fa-images text-[10px]"></i>
                                                <span>{block.media.length}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-3 flex flex-col flex-grow">
                                        <div className="mb-2">
                                            <span className="text-[9px] text-white/60 uppercase font-bold tracking-wider">{adCat}</span>
                                            <h4 className="text-xs font-bold text-white line-clamp-2 h-8 leading-tight">{adTitle}</h4>
                                            {adSubtitle && !adProduct && (
                                                <p className="text-[10px] text-white/70 line-clamp-1">{adSubtitle}</p>
                                            )}
                                        </div>
                                        <div className="mt-auto flex justify-between items-end">
                                            <p className="text-white font-extrabold text-sm">{adPrice ? formatPrice(adPrice) : (adProduct ? 'View' : (adSubtitle || 'View'))}</p>
                                            {adProduct && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onAddToBasket(adProduct, 1); }}
                                                    className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors shadow-sm"
                                                >
                                                    <i className="fas fa-plus text-xs"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 3. Explore Categories - Professional Grid Layout with Expandable Dropdown */}
            <div className="px-4 mb-8 bg-gradient-to-b from-transparent to-white/5 py-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-white text-xl">Explore Categories</h3>
                    <div className="text-gray-400 text-sm">Find what you need</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {categoryColumns.map((col, idx) => (
                        <div key={idx} className="relative">
                            <div 
                                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 hover:bg-white/20 transition-all duration-300 cursor-pointer group"
                                onClick={() => setExpandedCategory(expandedCategory === idx ? null : idx)}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className={`${col.bg} ${col.color} w-16 h-16 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
                                        <i className={`${col.icon} text-2xl`}></i>
                                    </div>
                                    <h4 className="font-bold text-white text-sm mb-2">{col.title}</h4>
                                    <p className="text-white/60 text-xs">Browse {col.items.length}+ sub-categories</p>
                                    <i className={`fas fa-chevron-down text-white/40 text-xs mt-2 transition-transform duration-300 ${expandedCategory === idx ? 'rotate-180' : ''}`}></i>
                                </div>
                            </div>
                            
                            {/* Expandable Dropdown */}
                            {expandedCategory === idx && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md border border-white/20 rounded-xl shadow-xl z-[9999] overflow-hidden">
                                    <div className="max-h-48 overflow-y-auto">
                                        {col.items.map((item, i) => (
                                            <div 
                                                key={i} 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(item.path);
                                                    setExpandedCategory(null);
                                                }}
                                                className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-gray-800">{item.name}</span>
                                                    <i className="fas fa-chevron-right text-gray-400 text-xs"></i>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* 4. Item of the Day - Special Card */}
            {itemOfTheDay && (
                <div className="px-4 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <i className="fas fa-star text-yellow-400"></i>
                        <h3 className="font-bold text-white text-lg">Item of the Day</h3>
                    </div>
                    <div 
                        onClick={() => onViewProduct(itemOfTheDay)}
                        className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl overflow-hidden relative cursor-pointer hover:bg-white/20 transition-all duration-300"
                    >
                        <div className="absolute top-3 right-3 z-10 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-1 rounded shadow-sm">
                            DAILY PICK
                        </div>
                        <div className="flex">
                            <div className="w-2/5 h-40 bg-white/10 relative">
                                <img src={optimizeImageSrc(cleanPath(itemOfTheDay.image_url_1) || 'images/products/Wooden Box.png', 800)} className="w-full h-full object-cover" alt={itemOfTheDay.name} />
                            </div>
                            <div className="w-3/5 p-4 flex flex-col justify-center bg-gradient-to-br from-white/10 to-transparent">
                                <span className="text-[10px] text-blue-400 font-bold uppercase tracking-wide mb-1">{itemOfTheDay.category_name || 'Featured'}</span>
                                <h4 className="font-bold text-white text-sm mb-2 line-clamp-2">{itemOfTheDay.name}</h4>
                                <div className="mt-auto">
                                    <p className="text-white/60 text-[10px] mb-0.5">Price</p>
                                    <p className="text-xl font-extrabold text-white">{formatPrice(itemOfTheDay.price)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 5. What's Hot (Horizontal Scroll) */}
            <div className="mb-8 py-6 bg-gradient-to-b from-white/5 to-transparent border-y border-white/10">
                <div className="px-4 flex justify-between items-center mb-4">
                    <h3 className="font-bold text-white text-lg">What's Hot</h3>
                </div>
                <div className="flex overflow-x-auto px-4 gap-4 pb-4 hide-scrollbar snap-x">
                    {loading ? (
                        [1,2,3,4].map(i => <div key={i} className="min-w-[160px] h-60 bg-gray-200 rounded-xl animate-pulse"></div>)
                    ) : (
                        deals.map(product => (
                            <div key={product.id} className="min-w-[160px] w-[160px] snap-start bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-all" onClick={() => onViewProduct(product)}>
                                <div className="h-36 bg-gray-100 relative group">
                                    <img src={optimizeImageSrc(cleanPath(product.image_url_1 || product.image_url_2 || product.image_url_3 || product.image_url_4) || 'images/products/Wooden Box.png', 400)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt={product.name}
                                    onError={(e) => {
                                        console.log(`Image load error for product ${product.id}:`, {
                                            name: product.name,
                                            listingType: product.listingType,
                                            image_url_1: product.image_url_1,
                                            image_url_2: product.image_url_2,
                                            image_url_3: product.image_url_3,
                                            image_url_4: product.image_url_4,
                                            images: product.images
                                        });
                                        e.target.src = optimizeImageSrc('images/products/Wooden Box.png', 400);
                                    }} />
                                    {/* Brand Badges */}
                                    {product.listingType === 'filter' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/KG-Filter-high-res-logo blue.png', 200)} alt="KG Filters" className="w-8 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.listingType === 'conveyor' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/arb-logo.png', 160)} alt="ARB" className="w-6 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.listingType === 'grease' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Ultra" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.listingType === 'kg_pps' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG ULTRA" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('KG') && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Bearings" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('FAG') && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/FAG.png', 120)} alt="FAG Bearings" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('NTN') && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/NTN.png', 120)} alt="NTN Bearings" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    <span className="absolute top-2 left-2 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-sm">NEW</span>
                                </div>
                                <div className="p-3">
                                    <h4 className="text-xs font-bold text-gray-800 truncate mb-1">{product.name}</h4>
                                    <p className="text-blue-600 font-bold text-sm">{formatPrice(product.price)}</p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* 6. Recommended Section */}
            <div className="px-4 mb-8">
                <h3 className="font-bold text-white text-lg mb-4">Recommended for You</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {loading ? (
                        [1,2,3,4,5,6].map(i => <div key={i} className="h-64 bg-white/10 rounded-xl animate-pulse border border-white/20"></div>)
                    ) : (
                        featuredProducts.map(product => (
                            <div key={product.id} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden cursor-pointer hover:bg-white/20 transition-all flex flex-col h-full" onClick={() => onViewProduct(product)}>
                                <div className="h-40 bg-white/10 relative overflow-hidden">
                                    <img src={optimizeImageSrc(cleanPath(product.image_url_1 || product.image_url_2 || product.image_url_3 || product.image_url_4) || 'images/products/Wooden Box.png', 800)} className="w-full h-full object-cover" alt={product.name}
                                    onError={(e) => {
                                        console.log(`Image load error for product ${product.id}:`, {
                                            name: product.name,
                                            listingType: product.listingType,
                                            image_url_1: product.image_url_1,
                                            image_url_2: product.image_url_2,
                                            image_url_3: product.image_url_3,
                                            image_url_4: product.image_url_4,
                                            images: product.images
                                        });
                                        e.target.src = optimizeImageSrc('images/products/Wooden Box.png', 400);
                                    }} />
                                    {/* Brand Badges */}
                                    {product.listingType === 'filter' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/KG-Filter-high-res-logo blue.png', 200)} alt="KG Filters" className="w-8 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.listingType === 'conveyor' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/arb-logo.png', 160)} alt="ARB" className="w-6 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.listingType === 'grease' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Ultra" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.listingType === 'kg_pps' && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG ULTRA" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('KG') && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Bearings" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('FAG') && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/FAG.png', 120)} alt="FAG Bearings" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('NTN') && (
                                        <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/NTN.png', 120)} alt="NTN Bearings" className="w-4 h-auto object-contain" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-3 flex flex-col flex-grow">
                                    <div className="mb-2">
                                        <span className="text-[9px] text-white/60 uppercase font-bold tracking-wider">{product.category_name}</span>
                                        <h4 className="text-xs font-bold text-white line-clamp-2 h-8 leading-tight">{product.name}</h4>
                                    </div>
                                    <div className="mt-auto flex justify-between items-end">
                                        <p className="text-white font-extrabold text-sm">{formatPrice(product.price)}</p>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); onAddToBasket(product, 1); }}
                                            className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors shadow-sm"
                                        >
                                            <i className="fas fa-plus text-xs"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* 7. Recommended Suppliers */}
            <div className="mb-8">
                <div className="px-4 mb-4">
                    <h3 className="font-bold text-white text-lg">Recommended Suppliers</h3>
                </div>
                <div className="flex overflow-x-auto px-4 gap-4 pb-2 hide-scrollbar">
                    {suppliers.map((brand, idx) => (
                        <div key={idx} onClick={() => {
                            try {
                                const formData = new FormData();
                                formData.append('supplier_name', brand.name);
                                navigator.sendBeacon('/api/track_supplier_click.php', formData);
                            } catch (e) { console.error("Could not track supplier click:", e); }
                            setSelectedSupplier(brand);
                        }} className="min-w-[100px] flex flex-col items-center gap-2 cursor-pointer">
                            <div className="w-20 h-20 rounded-lg bg-white backdrop-blur-sm border border-white/20 flex items-center justify-center p-3 transition-all hover:bg-white/30">
                                <img src={optimizeImageSrc(brand.logo, 120)} className="w-full h-full object-contain" alt={brand.name} />
                            </div>
                            <span className="text-xs font-medium text-white/60 text-center truncate w-full">{brand.name}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* 8. Admin Contact Details */}
            <div className="px-4 mt-8 mb-4">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                    <h3 className="text-white font-bold text-lg mb-1">Get in Touch</h3>
                    <p className="text-white/60 text-sm mb-4">Have questions? Reach out to our admin team.</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-white/80">
                        <a href="tel:+27824551658" className="flex items-center gap-2 hover:text-white transition-colors">
                            <i className="fas fa-phone text-blue-400"></i>
                            <span>+27 82 455 1658</span>
                        </a>
                        <span className="hidden sm:inline text-white/30">|</span>
                        <a href="mailto:info@minersmarket.co.za" className="flex items-center gap-2 hover:text-white transition-colors">
                            <i className="fas fa-envelope text-blue-400"></i>
                            <span>info@minersmarket.co.za</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Supplier Info Modal - same popup as marketplace suppliers */}
            {selectedSupplier && (
                <SupplierInfoModal
                    supplier={selectedSupplier}
                    onClose={() => setSelectedSupplier(null)}
                    navigate={navigate}
                />
            )}

            {/* Ad Block Media Viewer */}
            {viewerBlock && (
                <AdBlockMediaViewer
                    media={viewerBlock.media}
                    blockTitle={viewerBlock.title}
                    onClose={() => setViewerBlock(null)}
                />
            )}

                </div>
    );
}
